import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import apiConfig from '../config/apiConfig';
import { useAppState } from './AppStateContext';

export const UserContext = createContext();

// Add these mandatory circuit breakers at the top

// Hard circuit breakers to prevent runaway API calls
const CIRCUIT_BREAKERS = {
  lastReset: Date.now(),
  fetchCount: 0,
  maxFetchesPerMinute: 6,
  isBroken: false,
  breakUntil: 0
};

// Circuit breaker function to prevent API abuse
const checkCircuitBreaker = (force = false) => {
  const now = Date.now();
  
  // Reset counter if a minute has passed
  if (now - CIRCUIT_BREAKERS.lastReset > 60000) {
    CIRCUIT_BREAKERS.fetchCount = 0;
    CIRCUIT_BREAKERS.lastReset = now;
    CIRCUIT_BREAKERS.isBroken = false;
    console.log('Circuit breaker reset');
  }
  
  // Check if circuit is broken (time-based cooldown)
  if (CIRCUIT_BREAKERS.isBroken) {
    if (now < CIRCUIT_BREAKERS.breakUntil) {
      console.log(`Circuit breaker active for ${Math.round((CIRCUIT_BREAKERS.breakUntil - now)/1000)}s more`);
      return false; // Block all requests
    } else {
      // Reset breaker after cooldown period
      CIRCUIT_BREAKERS.isBroken = false;
      CIRCUIT_BREAKERS.fetchCount = 0;
      console.log('Circuit breaker reset after cooldown');
    }
  }
  
  // Count this request
  CIRCUIT_BREAKERS.fetchCount++;
  
  // Break circuit if too many requests and not forced
  if (!force && CIRCUIT_BREAKERS.fetchCount > CIRCUIT_BREAKERS.maxFetchesPerMinute) {
    console.log('🔴 CIRCUIT BREAKER ACTIVATED - Too many requests');
    CIRCUIT_BREAKERS.isBroken = true;
    CIRCUIT_BREAKERS.breakUntil = now + 300000; // Break for 5 minutes
    return false;
  }
  
  return true;
};

// Increase these values to reduce request frequency even further
const GLOBAL_REQUEST_TRACKER = {
  lastAccountFetch: 0,
  lastCharacterFetch: 0,
  isAccountFetching: false,
  isCharacterFetching: false,
  minInterval: 300000, // Increase to 5 minutes (from 2 minutes)
  sessionRequests: 0,
  maxRequestsPerSession: 15, // Reduce max requests (from 30)
  lastAlignmentCalculation: 0,
  alignmentCache: {} // Cache alignment calculations
};

// EMERGENCY KILL SWITCH - Global protection against API abuse
let API_KILLED = false;
let KILL_SWITCH_RESET_TIMER = null;

// Function to kill all API requests for a period of time
const killAllAPIRequests = (minutes = 10) => {
  if (API_KILLED) return; // Already killed
  
  console.error(`🔥 EMERGENCY: Killing all API requests for ${minutes} minutes due to potential infinite loop`);
  API_KILLED = true;
  
  // Set a timer to reset the kill switch
  if (KILL_SWITCH_RESET_TIMER) clearTimeout(KILL_SWITCH_RESET_TIMER);
  
  KILL_SWITCH_RESET_TIMER = setTimeout(() => {
    console.log('Kill switch reset after timeout');
    API_KILLED = false;
  }, minutes * 60 * 1000);
  
  // Also show a notification to the user
  if (typeof window !== 'undefined') {
    window.alert(`Application detected excessive API calls and has temporarily paused background requests for ${minutes} minutes to prevent server overload. You can continue using the app, but some data may not refresh automatically.`);
  }
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Fix the character stats state to properly include alignment values
  const [characterStats, setCharacterStats] = useState({
    level: 0,
    gold: 0,
    good: 0,
    neutral: 0, 
    evil: 0,
    experience: 0,
  });
  // Move all ref declarations to the top level of the component
  const lastRefreshTimeRef = useRef(0);
  const refreshingRef = useRef(false);
  const refreshDebounceTimer = useRef(null);
  const intervalRef = useRef(null);
  const resetTimerRef = useRef(null); // Move this declaration here
  
  const { updateCooldownsFromAPI } = useAppState();

  // Function to fetch detailed character data - optimized
  const fetchCharacterData = useCallback(async (characterId = null, force = false) => {
    // Circuit breaker - hard stop on runaway requests
    if (!checkCircuitBreaker(force)) {
      console.log('Character fetch blocked by circuit breaker');
      return;
    }

    // Global rate limiting check with enhanced throttling
    const now = Date.now();
    
    // Track session requests
    GLOBAL_REQUEST_TRACKER.sessionRequests++;
    if (GLOBAL_REQUEST_TRACKER.sessionRequests > GLOBAL_REQUEST_TRACKER.maxRequestsPerSession) {
      console.log(`Exceeded max requests per session (${GLOBAL_REQUEST_TRACKER.maxRequestsPerSession}), throttling further`);
      // Only allow requests every 5 minutes after hitting limit
      if (!force && now - GLOBAL_REQUEST_TRACKER.lastCharacterFetch < 300000) {
        return;
      }
    }
    
    if (!force && now - GLOBAL_REQUEST_TRACKER.lastCharacterFetch < GLOBAL_REQUEST_TRACKER.minInterval) {
      console.log('Global rate limit: Character fetch too frequent');
      return;
    }
    
    // Prevent overlapping requests
    if (GLOBAL_REQUEST_TRACKER.isCharacterFetching || (refreshingRef.current && !force)) {
      console.log('Skipping character fetch - already fetching');
      return;
    }
    
    // Use ref instead of state for lastRefreshTime
    if (!force && now - lastRefreshTimeRef.current < 10000) {
      console.log('Skipping character refresh - too soon since last refresh');
      return;
    }
    
    try {
      refreshingRef.current = true;
      GLOBAL_REQUEST_TRACKER.isCharacterFetching = true;
      GLOBAL_REQUEST_TRACKER.lastCharacterFetch = now;
      
      // Use the simpler /character/ endpoint when fetching own character
      const endpoint = characterId && user?.playerCharacter?.id !== characterId
        ? `${apiConfig.getCharacter}${characterId}` 
        : `${apiConfig.getCharacter}`;
      
      const response = await fetch(endpoint, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const characterData = await response.json();
        
        // Check if character is dead
        if (characterData.dead === true) {
          console.log('[UserContext] Character is dead, may need to create a new character');
        }
        
        // Set character stats in local state including all alignment values
        setCharacterStats({
          level: characterData.level || 0,
          gold: characterData.gold || 0,
          good: characterData.good || 0,
          neutral: characterData.neutral || 0,
          evil: characterData.evil || 0,
          experience: characterData.experience || 0,
        });
        
        // Extract cooldown information and update app state directly
        if (characterData.deedsCooldown !== undefined || characterData.inJail !== undefined) {
          console.log('[UserContext] Character data contains cooldown info, updating:', {
            deedsCooldown: characterData.deedsCooldown,
            inJail: characterData.inJail,
            jailReleaseTime: characterData.jailReleaseTime
          });
          
          // Only update if character data explicitly has these fields
          const cooldownData = {};
          if (characterData.deedsCooldown !== undefined) cooldownData.deedsCooldown = characterData.deedsCooldown;
          if (characterData.inJail !== undefined) cooldownData.inJail = characterData.inJail;
          if (characterData.jailReleaseTime !== undefined) cooldownData.jailReleaseTime = characterData.jailReleaseTime;
          
          // Only update if we actually have data to update
          if (Object.keys(cooldownData).length > 0) {
            updateCooldownsFromAPI(cooldownData);
          }
        }
        
        // Update user object with detailed character data
        setUser(prevUser => ({
          ...prevUser,
          playerCharacter: {
            ...prevUser?.playerCharacter,
            ...characterData,
            // Flag to clearly identify dead characters
            dead: characterData.dead === true,
            // Ensure these specific fields are properly set
            good: characterData.good || 0,
            neutral: characterData.neutral || 0,
            evil: characterData.evil || 0
          }
        }));
        
        // Update lastRefreshTime as ref instead of state
        lastRefreshTimeRef.current = now;

        // Add more detailed logging when character data is fetched
        if (characterData) {
          console.log('Raw character alignment data:', {
            good: characterData.good,
            neutral: characterData.neutral, 
            evil: characterData.evil,
            // Verify data types
            goodType: typeof characterData.good,
            neutralType: typeof characterData.neutral,
            evilType: typeof characterData.evil
          });
        }

        // Update last calculation timestamp
        GLOBAL_REQUEST_TRACKER.lastAlignmentCalculation = now;
      }
    } catch (error) {
      console.error('Error fetching character data:', error);
    } finally {
      refreshingRef.current = false;
      GLOBAL_REQUEST_TRACKER.isCharacterFetching = false;
      setLoading(false);
    }
  }, [updateCooldownsFromAPI, user]); // No need to add lastRefreshTimeRef as dependency

  // Function to fetch user account data with extra protection
  const fetchUserData = useCallback(async (force = false) => {
    // Add this check to prevent rapid fetching after deed completion
    if (window.PREVENT_AUTO_REFRESH && !force) {
      console.log('Skipping user data fetch - temporary refresh block active');
      return;
    }

    // Add debugging to trace repeated calls
    console.trace('fetchUserData called from:');

    // Hard prevention against cascading calls - emergency measure
    const now = Date.now();
    
    // SUPER AGGRESSIVE throttling - prevent calls within 2 seconds of each other
    if (now - GLOBAL_REQUEST_TRACKER.lastAccountFetch < 2000) {
      console.error('🛑 EMERGENCY THROTTLE: Multiple account fetch attempts within 2 seconds!');
      return;
    }
    
    // Circuit breaker check
    if (!checkCircuitBreaker(force)) {
      console.log('User data fetch blocked by circuit breaker');
      return;
    }

    // Global rate limiting check with enhanced throttling
    if (!force) {
      GLOBAL_REQUEST_TRACKER.sessionRequests++;
      if (GLOBAL_REQUEST_TRACKER.sessionRequests > GLOBAL_REQUEST_TRACKER.maxRequestsPerSession) {
        console.log(`Exceeded max requests per session (${GLOBAL_REQUEST_TRACKER.maxRequestsPerSession}), throttling further`);
        // Only allow requests every 5 minutes after hitting limit
        if (now - GLOBAL_REQUEST_TRACKER.lastAccountFetch < 300000) {
          return;
        }
      }
    }
    
    if (!force && now - GLOBAL_REQUEST_TRACKER.lastAccountFetch < GLOBAL_REQUEST_TRACKER.minInterval) {
      console.log('Global rate limit: Account fetch too frequent');
      return;
    }
    
    if (GLOBAL_REQUEST_TRACKER.isAccountFetching) {
      console.log('Skipping account fetch - already fetching');
      return;
    }
    
    try {
      // IMPORTANT: Mark as fetching BEFORE the async operation
      GLOBAL_REQUEST_TRACKER.isAccountFetching = true;
      GLOBAL_REQUEST_TRACKER.lastAccountFetch = now;
      
      console.log('🔄 Actually making account info request');
      const response = await fetch(apiConfig.accountInfo, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const userData = await response.json();
        
        // Check if user data contains a character and if the character is alive
        const hasCharacter = userData && userData.playerCharacter;
        const isCharacterDead = hasCharacter && userData.playerCharacter.dead === true;
        
        // Log character status to help with debugging
        console.log('Character status check:', { 
          hasCharacter, 
          isCharacterDead,
          characterData: hasCharacter ? userData.playerCharacter : 'No character'
        });
        
        setUser(userData);
        
        // If player character exists and is alive, fetch detailed character info
        if (hasCharacter && !isCharacterDead) {
          setCharacterStats({
            level: userData.playerCharacter.level || 0,
            gold: userData.playerCharacter.gold || 0,
            good: userData.playerCharacter.good || 0,
            neutral: userData.playerCharacter.neutral || 0,
            evil: userData.playerCharacter.evil || 0,
            experience: userData.playerCharacter.experience || 0,
          });
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setLoading(false);
    } finally {
      // IMPORTANT: Always clear the fetching flag
      GLOBAL_REQUEST_TRACKER.isAccountFetching = false;
    }
  }, []); // Only depend on fetchCharacterData

  // Fix the useEffect to use the ref instead of state
  useEffect(() => {
    console.log('UserProvider mounted - scheduling initial data fetch');
    
    // Create a flag to track whether this component is mounted
    let isMounted = true;
    
    // No need to declare resetTimerRef here - it's already declared at the top level
    
    // Define visibility change handler here to fix the reference
    const handleVisibilityChange = () => {
      if (!isMounted || API_KILLED) return;
      
      if (document.visibilityState === 'visible') {
        // Use ref instead of state
        const now = Date.now();
        // Increase minimum time between refreshes to 2 minutes
        if (now - lastRefreshTimeRef.current > 120000) { 
          fetchUserData(false); // Not forced - respect rate limits
        }
      }
    };
    
    // Use setTimeout with a longer delay for the initial fetch
    //const initialFetchTimer = setTimeout(() => {
    
    if (user == null)
    { 
      // Check for rapid re-render
      const now = Date.now();
      const lastMountTime = window._lastUserProviderMount || 0;
      
      if (now - lastMountTime < 1) {
        console.error('UserProvider remounted too quickly! Potential infinite loop.');
        killAllAPIRequests(15); // Kill API for 15 minutes
        return;
      }
      
      // Track mount time
      window._lastUserProviderMount = now;
      
    
      fetchUserData(true);
    }
    else
      setLoading(false);
    //}, 100); // Increased delay to prevent mount-time cascade
    
    // Set up a global refresh interval (increase from 5 minutes to 10 minutes)
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      // Only refresh if the page is visible and not killed
      if (document.visibilityState === 'visible' && !API_KILLED) {
        fetchUserData(false);
      }
    }, 600000); // 10 minutes
    
    // Set up session request counter reset using the ref from the top level
    resetTimerRef.current = setInterval(() => {
      if (GLOBAL_REQUEST_TRACKER.sessionRequests > 0) {
        console.log(`Resetting session request counter from ${GLOBAL_REQUEST_TRACKER.sessionRequests}`);
        GLOBAL_REQUEST_TRACKER.sessionRequests = 0;
      }
    }, 3600000); // Reset counter every hour
    
    // Handle page visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      isMounted = false;
      //clearTimeout(initialFetchTimer);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (resetTimerRef.current) clearInterval(resetTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUserData, user]); // Add fetchUserData as dependency

  // Update character stats directly without API call
  const updateCharacterStats = (newStats) => {
    setCharacterStats(prev => ({
      ...prev,
      ...newStats
    }));
  };

  // Update the refreshCharacterStats function to be more aggressive with throttling
  const refreshCharacterStats = useCallback(() => {
    // Circuit breaker - reject if breaker is active
    if (CIRCUIT_BREAKERS.isBroken) {
      console.log('Character refresh rejected - circuit breaker active');
      return;
    }

    // First, check if we really need to refresh
    const now = Date.now();
    
    // Clear any existing timer
    if (refreshDebounceTimer.current) {
      clearTimeout(refreshDebounceTimer.current);
      refreshDebounceTimer.current = null;
    }
    
    // Aggressive throttling - skip entirely if recent refresh (last 2 minutes)
    if (now - GLOBAL_REQUEST_TRACKER.lastCharacterFetch < 120000) {
      console.log('Skipping refresh entirely - refreshed in the last 2 minutes');
      return;
    }
    
    // Aggressive session limit throttling
    if (GLOBAL_REQUEST_TRACKER.sessionRequests > GLOBAL_REQUEST_TRACKER.maxRequestsPerSession / 2) {
      console.log('Session approaching request limit, using extreme throttling');
      
      // If we're past 50% of our request quota, only allow refresh every 10 minutes
      if (now - GLOBAL_REQUEST_TRACKER.lastCharacterFetch < 600000) {
        console.log('Throttled mode active - skipping refresh');
        return;
      }
    }
    
    // More aggressive debounce with longer delay during high activity
    const debounceTime = GLOBAL_REQUEST_TRACKER.sessionRequests > GLOBAL_REQUEST_TRACKER.maxRequestsPerSession / 2
      ? 30000 // 30 seconds if high activity
      : 15000; // 15 seconds normally
    
    // Set debounce timer
    console.log(`Setting debounce timer for ${debounceTime/1000}s`);
    refreshDebounceTimer.current = setTimeout(() => {
      if (!CIRCUIT_BREAKERS.isBroken && user?.playerCharacter?.id) {
        console.log('Refreshing character stats after debounce');
        fetchCharacterData(user.playerCharacter.id);
      }
      refreshDebounceTimer.current = null;
    }, debounceTime);
  }, [user, fetchCharacterData]);

  // Fix updateCharacter function which was improperly setting alignment
  const updateCharacter = (character) => {
    // Check if this update should skip auto-refresh
    const skipAutoRefresh = character?._skipAutoRefresh;
    
    // Remove the internal flag before updating state
    if (skipAutoRefresh && character._skipAutoRefresh) {
      delete character._skipAutoRefresh;
    }
    
    setUser(prevUser => ({
      ...prevUser,
      playerCharacter: {
        ...character,
        // Ensure alignment values are preserved
        good: character?.good || 0,
        neutral: character?.neutral || 0,
        evil: character?.evil || 0
      }
    }));
    
    if (character) {
      setCharacterStats({
        level: character.level || 0,
        gold: character.gold || 0,
        good: character.good || 0,
        neutral: character.neutral || 0,
        evil: character.evil || 0,
        experience: character.experience || 0,
      });
    }
    
    // If this update should skip auto-refresh, set the global flag
    if (skipAutoRefresh) {
      window.PREVENT_AUTO_REFRESH = true;
      setTimeout(() => {
        window.PREVENT_AUTO_REFRESH = false;
      }, 10000); // Allow refreshes again after 10 seconds
    }
  };

  return (
    <UserContext.Provider value={{ 
      user, 
      setUser, 
      loading,
      characterStats, 
      updateCharacterStats,
      refreshCharacterStats,
      updateCharacter,
      fetchUserData
    }}>
      {children}
    </UserContext.Provider>
  );
};
